import Link from '@/routing/AppLink';
import { useTranslation } from 'next-i18next/pages';

import {
  NEWS_CATEGORY_FILTER_LABEL_KEYS,
  type NewsCategoryFilterOption,
} from '@/utils/newsCategoryFilter';

import { trackNewsCategorySelection } from './newsFilterAnalytics';

type Props = {
  options: ReadonlyArray<NewsCategoryFilterOption>;
};

const ITEM_CLASS =
  'inline-flex min-h-11 cursor-pointer items-center justify-center whitespace-nowrap rounded-[50px] border border-solid px-4 py-2.5 text-sm font-medium leading-[160%] transition-colors duration-200 ease-out focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-apps-corePrimary01 motion-reduce:transition-none md:px-5 md:text-[15px]';

function itemClass(isSelected: boolean) {
  return [
    ITEM_CLASS,
    isSelected
      ? 'border-apps-corePrimary01 bg-apps-corePrimary05 text-apps-corePrimary01'
      : 'border-apps-coreProductBorder text-apps-coreGray01 hover:border-apps-corePrimary01 hover:text-apps-corePrimary01',
  ].join(' ');
}

/**
 * ### NewsCategoryFilter
 *
 * Renders the newsroom category controls as links, so the chosen filter lives
 * in the URL and survives sharing and reloading. Filtering itself happens on
 * the server; this component only navigates and reports the choice to
 * analytics. An empty option list renders nothing.
 *
 * @param props.options Controls to render, unfiltered option first
 * @returns The labelled filter navigation, or nothing when there is no choice to offer
 */
export default function NewsCategoryFilter({ options }: Props) {
  const { t } = useTranslation('common');

  if (options.length === 0) return null;

  return (
    <nav
      aria-label={t('뉴스 분야 필터')}
      className="mb-8 flex flex-wrap gap-2 lg:mb-10"
    >
      {options.map((option) => (
        <Link
          aria-current={option.isSelected ? 'page' : undefined}
          className={itemClass(option.isSelected)}
          href={option.href}
          key={option.id}
          onClick={() => trackNewsCategorySelection(option.id)}
          prefetch={false}
          scroll={false}
        >
          {t(NEWS_CATEGORY_FILTER_LABEL_KEYS[option.id])}
        </Link>
      ))}
    </nav>
  );
}
